import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import api from '../services/api';
import { Plus, Check, X, RefreshCw, FileText, DollarSign, Calendar, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const VacationManagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canView = hasPermission(user?.role, 'vacations:view') || hasPermission(user?.role, 'vacations:full') || hasPermission(user?.role, 'financial:full');
  const canEdit = hasPermission(user?.role, 'vacations:full') || hasPermission(user?.role, 'financial:full');

  const [activeTab, setActiveTab] = useState('requests');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    vacation_period_id: '',
    start_date: '',
    end_date: '',
    total_days: 0,
    reason: ''
  });

  const [selectedPayslipForPay, setSelectedPayslipForPay] = useState(null);
  const [payModalData, setPayModalData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Transferência'
  });

  // Buscar solicitações de férias
  const { data: requestsData, isLoading: loadingRequests } = useQuery({
    queryKey: ['vacation-requests'],
    queryFn: async () => {
      const { data } = await api.get('/vacations/requests');
      return data;
    },
    enabled: canView
  });

  // Buscar períodos aquisitivos
  const { data: periodsData, isLoading: loadingPeriods } = useQuery({
    queryKey: ['vacation-periods'],
    queryFn: async () => {
      const { data } = await api.get('/vacations/periods');
      return data;
    },
    enabled: canView
  });

  // Buscar recibos de pagamento de férias
  const { data: payslipsData, isLoading: loadingPayslips } = useQuery({
    queryKey: ['vacation-payslips'],
    queryFn: async () => {
      const { data } = await api.get('/vacation-payslips');
      return data;
    },
    enabled: canView
  });

  // Buscar usuários
  const { data: usersData } = useQuery({
    queryKey: ['users-mini'],
    queryFn: () => api.get('/users?limit=500&active=true'),
    enabled: canEdit
  });

  const requests = requestsData?.data || [];
  const periods = periodsData?.data || [];
  const payslips = payslipsData?.data || [];
  const users = usersData?.data?.users || [];

  // Mutation para auto-sincronizar períodos aquisitivos por hire_date
  const autoSyncMutation = useMutation({
    mutationFn: () => api.post('/vacations/periods/auto-sync'),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['vacation-periods'] });
      toast.success(res.data?.message || 'Períodos aquisitivos sincronizados com sucesso!');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao sincronizar períodos aquisitivos');
    }
  });

  // Mutation para aprovar recibo de férias
  const approvePayslipMutation = useMutation({
    mutationFn: (id) => api.put(`/vacation-payslips/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-payslips'] });
      toast.success('Recibo de férias aprovado!');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao aprovar recibo de férias');
    }
  });

  // Mutation para pagar recibo de férias e lançar no caixa
  const payPayslipMutation = useMutation({
    mutationFn: ({ id, payment_date, payment_method }) =>
      api.put(`/vacation-payslips/${id}/pay`, { payment_date, payment_method }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-payslips'] });
      toast.success('Pagamento de férias registrado e lançado no Livro Caixa!');
      setSelectedPayslipForPay(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao registrar pagamento');
    }
  });

  // Mutation para criar solicitação
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/vacations/requests', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-requests'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-periods'] });
      queryClient.invalidateQueries({ queryKey: ['vacation-payslips'] });
      toast.success('Solicitação de férias enviada com sucesso!');
      setShowModal(false);
      setFormData({ user_id: '', vacation_period_id: '', start_date: '', end_date: '', total_days: 0, reason: '' });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao enviar solicitação');
    }
  });

  // Mutation para aprovar
  const approveMutation = useMutation({
    mutationFn: (id) => api.put(`/vacations/requests/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-requests'] });
      toast.success('Férias aprovadas!');
    },
    onError: () => {
      toast.error('Erro ao aprovar');
    }
  });

  // Mutation para rejeitar
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => api.put(`/vacations/requests/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-requests'] });
      toast.success('Solicitação rejeitada');
    },
    onError: () => {
      toast.error('Erro ao rejeitar');
    }
  });

  // Mutation para criar período aquisitivo
  const createPeriodMutation = useMutation({
    mutationFn: (data) => api.post('/vacations/periods', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-periods'] });
      toast.success('Período aquisitivo criado!');
      setShowModal(false);
    },
    onError: () => {
      toast.error('Erro ao criar período');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'requests') {
      createMutation.mutate(formData);
    } else {
      createPeriodMutation.mutate(formData);
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      PENDING: 'Pendente',
      APPROVED: 'Aprovada',
      REJECTED: 'Rejeitada',
      CANCELLED: 'Cancelada'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Você não tem permissão para visualizar férias.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Férias</h1>
          <p className="text-gray-600">Controle de períodos aquisitivos, solicitações e recibos com lançamento no caixa</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => autoSyncMutation.mutate()}
              disabled={autoSyncMutation.isLoading}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-100 text-sm font-medium transition-colors"
              title="Sincronizar períodos aquisitivos de 12 meses para todos os funcionários a partir da data de admissão (CLT)"
            >
              <RefreshCw className={`w-4 h-4 ${autoSyncMutation.isLoading ? 'animate-spin' : ''}`} />
              Sincronizar Períodos
            </button>
            <button
              onClick={() => {
                setFormData({
                  user_id: '',
                  vacation_period_id: '',
                  start_date: '',
                  end_date: '',
                  total_days: 0,
                  reason: ''
                });
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Plus className="w-5 h-5" />
              Nova Solicitação
            </button>
          </div>
        )}
      </div>

      {/* Abas */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Solicitações
            {requests.filter(r => r.status === 'PENDING').length > 0 && (
              <span className="ml-2 bg-yellow-100 text-yellow-600 py-0.5 px-2 rounded-full text-xs">
                {requests.filter(r => r.status === 'PENDING').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('periods')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'periods'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Períodos Aquisitivos
          </button>
          <button
            onClick={() => setActiveTab('receipts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'receipts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Recibos de Férias
            {payslips.filter(p => p.status === 'PENDING' || p.status === 'APPROVED').length > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-700 py-0.5 px-2 rounded-full text-xs">
                {payslips.filter(p => p.status === 'PENDING' || p.status === 'APPROVED').length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Lista de Solicitações */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loadingRequests ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : requests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Nenhuma solicitação encontrada</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Período</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dias</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.start_date && new Date(request.start_date).toLocaleDateString('pt-BR')}
                      {' - '}
                      {request.end_date && new Date(request.end_date).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.total_days} dias
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {canEdit && request.status === 'PENDING' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => approveMutation.mutate(request.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Aprovar"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Motivo da rejeição:');
                              if (reason) {
                                rejectMutation.mutate({ id: request.id, reason });
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Rejeitar"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Lista de Períodos Aquisitivos */}
      {activeTab === 'periods' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loadingPeriods ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : periods.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Nenhum período aquisitivo encontrado</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Período Aquisitivo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Limite Concessivo (Art. 134 CLT)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo Disponível</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Situação CLT / Risco de Dobro</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {periods.map((period) => {
                  const getCltBadge = () => {
                    if (period.clt_status === 'EXPIRED_DOUBLE_DUE') {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300 inline-flex items-center gap-1">
                          🚨 Dobro Devido (Vencido há {Math.abs(period.days_until_double_penalty)}d)
                        </span>
                      );
                    }
                    if (period.clt_status === 'WARNING_DOUBLE_RISK') {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1">
                          ⚠️ Vence em {period.days_until_double_penalty}d (Risco Art. 137)
                        </span>
                      );
                    }
                    if (period.clt_status === 'IN_CONCESSIVE') {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          Concessivo Regular ({period.days_until_double_penalty}d restantes)
                        </span>
                      );
                    }
                    if (period.clt_status === 'ACQUIRING') {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Aquisitivo em Andamento
                        </span>
                      );
                    }
                    if (period.clt_status === 'COMPLETED') {
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Férias Totalmente Gozadas
                        </span>
                      );
                    }
                    return (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        period.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {period.status === 'ACTIVE' ? 'Ativo' : period.status}
                      </span>
                    );
                  };

                  return (
                    <tr key={period.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {period.user_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {period.start_date && new Date(period.start_date).toLocaleDateString('pt-BR')} até {period.end_date && new Date(period.end_date).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {period.concessive_limit_date ? new Date(period.concessive_limit_date).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                        {period.available_days} / {period.total_days} dias
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCltBadge()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Lista de Recibos de Férias */}
      {activeTab === 'receipts' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loadingPayslips ? (
            <div className="p-8 text-center text-gray-500">Carregando recibos...</div>
          ) : payslips.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Nenhum recibo de férias gerado até o momento. Recibos são gerados automaticamente na aprovação das solicitações.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Colaborador</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref / Dias</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Dias</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">1/3 Férias (+)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descontos (-)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Líquido a Pagar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payslips.map((p) => {
                  const totalDeductions = parseFloat(p.inss_deduction || 0) + parseFloat(p.irrf_deduction || 0);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {p.user_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {String(p.ref_month).padStart(2, '0')}/{p.ref_year} ({p.days_count} dias)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {Number(p.total_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                        + {Number(p.one_third_bonus || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                        {totalDeductions > 0 ? `- ${totalDeductions.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : 'R$ 0,00'}
                        {p.inss_deduction > 0 && <span className="block text-[11px] text-gray-500">INSS: R$ {Number(p.inss_deduction).toFixed(2)}</span>}
                        {p.irrf_deduction > 0 && <span className="block text-[11px] text-gray-500">IRRF: R$ {Number(p.irrf_deduction).toFixed(2)}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-emerald-700">
                        {Number(p.net_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          p.status === 'PAID'
                            ? 'bg-emerald-100 text-emerald-800'
                            : p.status === 'APPROVED'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                        }`}>
                          {p.status === 'PAID' ? 'Pago no Caixa' : p.status === 'APPROVED' ? 'Aprovado' : 'Pendente'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            {p.status === 'PENDING' && (
                              <button
                                onClick={() => approvePayslipMutation.mutate(p.id)}
                                disabled={approvePayslipMutation.isLoading}
                                className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-medium"
                              >
                                Aprovar Recibo
                              </button>
                            )}
                            {p.status === 'APPROVED' && (
                              <button
                                onClick={() => setSelectedPayslipForPay(p)}
                                className="px-2.5 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 font-medium shadow-sm"
                              >
                                Pagar & Baixar no Caixa
                              </button>
                            )}
                            {p.status === 'PAID' && p.payment_date && (
                              <span className="text-xs text-gray-500">
                                Pago em {new Date(p.payment_date).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal de Pagamento de Férias e Baixa no Caixa */}
      {selectedPayslipForPay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Pagamento de Férias</h3>
              <button onClick={() => setSelectedPayslipForPay(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
              <p className="text-xs text-emerald-700 font-medium">Colaborador</p>
              <p className="text-base font-bold text-emerald-950">{selectedPayslipForPay.user_name}</p>
              <p className="text-xs text-emerald-800 mt-1">
                Ref: {selectedPayslipForPay.ref_month}/{selectedPayslipForPay.ref_year} ({selectedPayslipForPay.days_count} dias de férias)
              </p>
              <p className="text-lg font-black text-emerald-700 mt-2">
                Valor Líquido: {Number(selectedPayslipForPay.net_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento</label>
                <input
                  type="date"
                  value={payModalData.payment_date}
                  onChange={(e) => setPayModalData({ ...payModalData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pagamento</label>
                <select
                  value={payModalData.payment_method}
                  onChange={(e) => setPayModalData({ ...payModalData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="Transferência">Transferência Bancária</option>
                  <option value="PIX">PIX</option>
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              <p className="text-xs text-gray-500">
                💡 O valor líquido será lançado automaticamente como saída no Livro Caixa e abaterá o saldo da empresa.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                type="button"
                onClick={() => setSelectedPayslipForPay(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => payPayslipMutation.mutate({
                  id: selectedPayslipForPay.id,
                  payment_date: payModalData.payment_date,
                  payment_method: payModalData.payment_method
                })}
                disabled={payPayslipMutation.isLoading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium shadow-sm"
              >
                {payPayslipMutation.isLoading ? 'Processando...' : 'Confirmar & Lançar no Caixa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {activeTab === 'requests' ? 'Nova Solicitação de Férias' : 'Novo Período Aquisitivo'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value, vacation_period_id: '' })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Selecione...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>

              {activeTab === 'requests' && formData.user_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Período Aquisitivo de Origem</label>
                  <select
                    value={formData.vacation_period_id}
                    onChange={(e) => {
                      const periodId = e.target.value;
                      setFormData({ ...formData, vacation_period_id: periodId });
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Selecione o período aquisitivo...</option>
                    {periods
                      .filter(p => String(p.user_id) === String(formData.user_id) && Number(p.available_days) > 0)
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {new Date(p.start_date).toLocaleDateString('pt-BR')} a {new Date(p.end_date).toLocaleDateString('pt-BR')} — Saldo: {p.available_days} dias
                        </option>
                      ))}
                  </select>
                  {periods.filter(p => String(p.user_id) === String(formData.user_id) && Number(p.available_days) > 0).length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Nenhum período aquisitivo com saldo ativo encontrado. Clique em "Sincronizar Períodos" no topo da página.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value, total_days: calculateDays(e.target.value, formData.end_date) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value, total_days: calculateDays(formData.start_date, e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total de Dias Corridos (CLT)</label>
                <input
                  type="number"
                  value={formData.total_days}
                  onChange={(e) => setFormData({ ...formData, total_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                  max="30"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo/Observação</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacationManagement;
